/**
 * RAReviewSchedule - Review Status Dashboard for MCA Compliance
 *
 * Shows all risk assessments with their review status for quick
 * compliance verification by MCA inspectors.
 *
 * @module components/RAReviewSchedule
 */

import React, { useMemo } from 'react';
import {
  Download,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import BackButton from '../../../components/tailwind/common/BackButton';
import { toLocalYMD } from '../../../utils/dateFmt';

/**
 * Calculate review status for display
 */
const getReviewStatusInfo = (assessment) => {
  if (!assessment.nextReviewDate) {
    return {
      status: 'not_set',
      label: 'Not Scheduled',
      color: 'gray',
      priority: 4
    };
  }

  if (assessment.status !== 'active') {
    return {
      status: 'inactive',
      label: `${assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1)}`,
      color: 'gray',
      priority: 5
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const reviewDate = new Date(assessment.nextReviewDate);
  reviewDate.setHours(0, 0, 0, 0);
  const daysUntil = Math.ceil((reviewDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntil < 0) {
    return {
      status: 'overdue',
      label: `${Math.abs(daysUntil)} days overdue`,
      daysUntil,
      color: 'red',
      priority: 1
    };
  } else if (daysUntil <= 30) {
    return {
      status: 'due_soon',
      label: daysUntil === 0 ? 'Due today' : `Due in ${daysUntil} days`,
      daysUntil,
      color: 'amber',
      priority: 2
    };
  } else {
    return {
      status: 'current',
      label: `Due in ${daysUntil} days`,
      daysUntil,
      color: 'green',
      priority: 3
    };
  }
};

/**
 * RAReviewSchedule Component
 */
const RAReviewSchedule = ({ onBack, assessments = [], vesselName = '' }) => {
  // Calculate status for each assessment and sort by priority
  const reviewData = useMemo(() => {
    return assessments
      .filter(a => a.status !== 'archived') // Exclude archived
      .map(assessment => ({
        ...assessment,
        reviewInfo: getReviewStatusInfo(assessment)
      }))
      .sort((a, b) => {
        // Sort by priority (overdue first), then by days until due
        if (a.reviewInfo.priority !== b.reviewInfo.priority) {
          return a.reviewInfo.priority - b.reviewInfo.priority;
        }
        return (a.reviewInfo.daysUntil || 999) - (b.reviewInfo.daysUntil || 999);
      });
  }, [assessments]);

  // Summary counts
  const summary = useMemo(() => ({
    overdue: reviewData.filter(a => a.reviewInfo.status === 'overdue').length,
    dueSoon: reviewData.filter(a => a.reviewInfo.status === 'due_soon').length,
    current: reviewData.filter(a => a.reviewInfo.status === 'current').length,
    notSet: reviewData.filter(a => a.reviewInfo.status === 'not_set').length,
    total: reviewData.length
  }), [reviewData]);

  // Export to CSV
  const handleExport = () => {
    const headers = ['Assessment', 'Status', 'Last Reviewed', 'Next Review', 'Review Status', 'Days Until Due'];
    const rows = reviewData.map(a => [
      a.title || 'Untitled',
      a.status || '',
      a.reviewedAt ? toLocalYMD(new Date(a.reviewedAt)) : 'Never',
      a.nextReviewDate ? toLocalYMD(new Date(a.nextReviewDate)) : 'Not Set',
      a.reviewInfo.label,
      a.reviewInfo.daysUntil ?? ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ra-review-schedule-${toLocalYMD(new Date())}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (reviewInfo) => {
    const colors = {
      red: 'bg-red-100 text-red-800 border-red-200',
      amber: 'bg-amber-100 text-amber-800 border-amber-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      gray: 'bg-gray-100 text-gray-600 border-gray-200'
    };

    const icons = {
      overdue: <AlertTriangle className="h-3 w-3" />,
      due_soon: <Clock className="h-3 w-3" />,
      current: <CheckCircle className="h-3 w-3" />,
      not_set: null,
      inactive: null
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colors[reviewInfo.color]}`}>
        {icons[reviewInfo.status]}
        {reviewInfo.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Review Schedule
                </h1>
                <p className="text-sm text-gray-500">
                  {vesselName ? `${vesselName} - ` : ''}{summary.total} active assessments
                </p>
              </div>
            </div>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className={`bg-white rounded-xl border-2 p-4 text-center ${summary.overdue > 0 ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
            <div className={`text-3xl font-bold ${summary.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {summary.overdue}
            </div>
            <div className="text-sm text-gray-600 flex items-center justify-center gap-1">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Overdue
            </div>
          </div>
          <div className={`bg-white rounded-xl border-2 p-4 text-center ${summary.dueSoon > 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
            <div className={`text-3xl font-bold ${summary.dueSoon > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
              {summary.dueSoon}
            </div>
            <div className="text-sm text-gray-600 flex items-center justify-center gap-1">
              <Clock className="h-4 w-4 text-amber-500" />
              Due Soon (30 days)
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-gray-200 p-4 text-center">
            <div className="text-3xl font-bold text-green-600">
              {summary.current}
            </div>
            <div className="text-sm text-gray-600 flex items-center justify-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Current
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-gray-200 p-4 text-center">
            <div className="text-3xl font-bold text-gray-400">
              {summary.notSet}
            </div>
            <div className="text-sm text-gray-600">
              Not Scheduled
            </div>
          </div>
        </div>

        {/* Compliance Alert */}
        {summary.overdue > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800">Compliance Alert</h3>
                <p className="text-sm text-red-700 mt-1">
                  {summary.overdue} risk assessment{summary.overdue !== 1 ? 's are' : ' is'} overdue for review.
                  WBC3 Section 3.14 requires risk assessments to be reviewed periodically.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Assessment Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="px-4 py-3 text-left text-sm font-medium">Assessment</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Last Reviewed</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Next Review</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Review Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reviewData.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="font-medium">No active risk assessments</p>
                      <p className="text-sm mt-1">Create risk assessments to track their review schedule</p>
                    </td>
                  </tr>
                ) : (
                  reviewData.map((assessment) => (
                    <tr key={assessment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {assessment.title || 'Untitled Assessment'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {assessment.category || 'No category'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="capitalize text-sm text-gray-600">
                          {assessment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {assessment.reviewedAt ? (
                          <div>
                            <div>{formatDate(assessment.reviewedAt)}</div>
                            {assessment.reviewedBy && (
                              <div className="text-xs text-gray-400">by {assessment.reviewedBy}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">Never reviewed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(assessment.nextReviewDate)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(assessment.reviewInfo)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-4 text-center text-xs text-gray-500">
          WBC3 Section 3.14.2: Risk assessments should be reviewed when operational changes occur
          and at intervals appropriate to the risk level.
        </div>
      </div>
    </div>
  );
};

export default RAReviewSchedule;
